import { FilterX, X, FileCheck } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"

import { ProjectDataTable } from "@/components/datatables/ProjectDataTable"
import { FiltersPanel } from "@/components/filters/FiltersPanel"
import { CONFIGURATION } from "@env/variables"
import { REPORT_GENERATION_FACETS } from "@/constants/facets"
import { transformPropertiesFacetsWithFilter, transformFacetStatusResponses, transformFacetTypeResponses } from "@/lib/facets"
import { removeEmptyValues } from "@/lib/filters"
import { FacetResponse, PropertyFacet, SearchFilters } from "@/models/facets"
import { DynamicColumn } from "@/models/datatable"
import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@/plugins/providers/document/DocumentSearchContext"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"

import { ContentObjectItem, VectorSearchQuery } from "@vertesia/common"

const COLUMNS: DynamicColumn[] = [
    { key: 'title', label: 'Title', type: 'string' },
    { key: 'type.name', label: 'Document Type', type: 'string' },
    { key: 'created_at', label: 'Created At', type: 'date' },
    { key: 'updated_at', label: 'Updated At', type: 'date' }
]

interface AdditionalDocumentsSelectionSearchPanelProps {
    children?: React.ReactNode
    onMultipleSelection: (documents: ContentObjectItem[]) => void
}
export function AdditionalDocumentsSelectionSearchPanel({ children, onMultipleSelection }: Readonly<AdditionalDocumentsSelectionSearchPanelProps>) {
    const { hasMore, search, isSearching, error, objects } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const { typeRegistry } = useUserSession()
    const documentTypes = useMemo(() => typeRegistry?.types || [], [typeRegistry?.types])
    const filteredDocumentTypes = useMemo(() => documentTypes.filter((type) => !CONFIGURATION.TYPES_TO_EXCLUDE.includes(type.id)), [documentTypes])

    const [filters, setFilters] = useState<SearchFilters>({})
    const [statuses, setStatuses] = useState<FacetResponse[]>([])
    const [properties, setProperties] = useState<PropertyFacet[]>([])
    const [types, setTypes] = useState<FacetResponse[]>([])
    const [users, setUsers] = useState<FacetResponse[]>([])

    const [selectionDocuments, setSelectionDocuments] = useState<ContentObjectItem[]>([])

    const [loadingMore, setLoadingMore] = useState(false)

    const [clearFilters, setClearFilters] = useState(0)

    const searchObjects = async () => {
        const full_text = filters?.properties?.full_text
        const vectorText = filters?.vectorText as string
        const vector: VectorSearchQuery | undefined = vectorText ? {text: vectorText, config: {text: true, image: true, properties: true}} : undefined
        
        const properties = removeEmptyValues(filters?.properties)

        const query: any = filters
            ? {
                createdFrom: filters?.date?.createdFrom,
                createdTo: filters?.date?.createdTo,
                updatedFrom: filters?.date?.updatedFrom,
                updatedTo: filters?.date?.updatedTo,
                full_text,
                name: filters?.property?.name,
                status: filters?.status,
                types: filters?.type ? [filters.type] : filteredDocumentTypes.map((type) => type.id),
                vector,
                match: {
                    created_by: filters?.user,
                    ...properties
                }
            } : {}

        search.query = query

        search.search()
    }

    const loadMoreDocuments = () => {
        setLoadingMore(true)

        search
            .loadMore()
            .then(() => {
                setLoadingMore(false)
            })
            .catch(() => {
                setLoadingMore(false)
            })
    }

    const handleOnClearFilters = () => {
        setClearFilters(clearFilters + 1)
    }

    const handleMultipleSelection = useCallback((documents: ContentObjectItem[]) => {
        setSelectionDocuments(documents)
    }, [])

    const confirmMultipleSelection = useCallback(() => {
        onMultipleSelection(selectionDocuments)
    }, [onMultipleSelection, selectionDocuments])

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        if (facets?.total) {
            delete facets.total
        }
        setTypes(transformFacetTypeResponses(facets.type, typeRegistry))
        setStatuses(transformFacetStatusResponses(facets.status))
        setUsers(transformFacetStatusResponses((facets as any).created_by))
        setProperties(transformPropertiesFacetsWithFilter(facets, REPORT_GENERATION_FACETS))
    }, [facets])

    useEffect(() => {
        searchObjects()
    }, [])

    return (
        <div className="flex h-full w-full">
            <FiltersPanel properties={properties} statuses={statuses} types={types} users={users} onFiltersChange={setFilters} onSearch={() => searchObjects()} onClearFilters={clearFilters} />
            <div className='w-full h-full flex flex-col gap-2'>
                <div className='w-full flex flex-row gap-2 items-center justify-end p-2 pb-0'>
                    <div className='flex h-10 gap-2 items-center'>
                        {
                            (selectionDocuments.length !== 0) &&
                            <>
                                <span className='text-sm text-gray-500'>{selectionDocuments.length} project{selectionDocuments.length > 1 ? "s" : ""} selected</span>
                                <Button variant='ghost' className='text-gray-500' onClick={() => {
                                    setSelectionDocuments([])
                                }}>
                                    <X />
                                </Button>
                                <Button onClick={() => {
                                    confirmMultipleSelection()
                                }}>
                                    <FileCheck />
                                    Confirm Selection
                                </Button>
                            </>
                        }
                        {
                            filters && (Object.keys(filters?.properties ?? {}).length > 0 || Object.keys(filters?.matches ?? {}).length > 0) &&
                            <Button onClick={handleOnClearFilters}>
                                <FilterX />
                                Clear Filters
                            </Button>
                        }
                        {children}
                    </div>
                </div>
                <div className="flex-grow w-full overflow-auto px-2">
                    <ProjectDataTable
                        columns={COLUMNS}
                        data={objects}
                        loading={isSearching}
                        loadingMore={loadingMore}
                        hasMore={hasMore}
                        loadMore={loadMoreDocuments}
                        onMultipleSelection={handleMultipleSelection}
                        selection={selectionDocuments}
                    />
                </div>
            </div>
        </div>
    )
}